import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../utils/apiUtils';

type PermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

const isSupported = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
};

const urlBase64ToUint8Array = (base64: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(normalized);
  const output = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) output[i] = rawData.charCodeAt(i);
  return output;
};

const arrayBufferToBase64 = (buffer: ArrayBuffer | null): string => {
  if (!buffer) return '';
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i += 1) binary += String.fromCharCode(bytes[i]);
  return window.btoa(binary);
};

export interface UseWebPushResult {
  supported: boolean;
  permission: PermissionState;
  isSubscribed: boolean;
  loading: boolean;
  error: string | null;
  subscribe: () => Promise<boolean>;
  unsubscribe: () => Promise<boolean>;
}

export function useWebPush(): UseWebPushResult {
  const supported = useMemo(isSupported, []);

  const [permission, setPermission] = useState<PermissionState>(() => {
    if (!supported) return 'unsupported';
    return (Notification.permission as PermissionState) ?? 'default';
  });
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(supported);
  const [error, setError] = useState<string | null>(null);

  const refreshSubscriptionState = useCallback(async () => {
    if (!supported) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setIsSubscribed(Boolean(sub));
    } catch (_e) {
      setIsSubscribed(false);
    } finally {
      setLoading(false);
    }
  }, [supported]);

  useEffect(() => {
    if (!supported) {
      setLoading(false);
      return;
    }
    refreshSubscriptionState();
  }, [supported, refreshSubscriptionState]);

  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!supported) {
      setError('Tu navegador no soporta notificaciones push');
      return false;
    }
    setError(null);
    setLoading(true);
    try {
      const result = await Notification.requestPermission();
      setPermission(result as PermissionState);
      if (result !== 'granted') {
        setError(result === 'denied' ? 'Activá las notificaciones en la configuración del navegador' : null);
        return false;
      }

      const vapidRes = await apiRequest<{ publicKey: string }>('/api/notifications/vapid-public-key', {
        method: 'GET',
        requireAuth: false,
      });
      if (vapidRes.error || !vapidRes.data?.publicKey) {
        setError('Web push no está disponible por ahora');
        return false;
      }

      const reg = await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      const subscription =
        existing ||
        (await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidRes.data.publicKey) as unknown as BufferSource,
        }));

      const payload = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: arrayBufferToBase64(subscription.getKey('p256dh') as ArrayBuffer | null),
          auth: arrayBufferToBase64(subscription.getKey('auth') as ArrayBuffer | null),
        },
      };

      const saveRes = await apiRequest('/api/users/web-push-subscription', { method: 'POST', body: payload });
      if (saveRes.error) {
        setError('No se pudo guardar la suscripción en el servidor');
        return false;
      }

      setIsSubscribed(true);
      return true;
    } catch (e: any) {
      setError(e?.message || 'No se pudo activar las notificaciones');
      return false;
    } finally {
      setLoading(false);
    }
  }, [supported]);

  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!supported) return false;
    setError(null);
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (!sub) {
        setIsSubscribed(false);
        return true;
      }

      await apiRequest('/api/users/web-push-subscription', {
        method: 'DELETE',
        body: { endpoint: sub.endpoint },
      }).catch(() => null);

      await sub.unsubscribe();
      setIsSubscribed(false);
      return true;
    } catch (e: any) {
      setError(e?.message || 'No se pudo desactivar');
      return false;
    } finally {
      setLoading(false);
    }
  }, [supported]);

  return { supported, permission, isSubscribed, loading, error, subscribe, unsubscribe };
}
